import { motion } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DraftOptions, usePoolSize } from '@/components/DraftOptions';

export function CpuSetupScreen() {
  const { setPhase, startGame, updateSettings, settings } = useGameStore();
  const auctionCountOptions: Array<10 | 15 | 20 | 30> = [10, 15, 20, 30];
  const startingCoinOptions = [3000, 5000, 8000, 10000];
  const { available } = usePoolSize(settings);
  const poolTooSmall = available < settings.auctionCount;

  const handleStart = () => {
    startGame('cpu');
  };

  return (
    <div className="min-h-screen bg-arena flex flex-col items-center justify-center p-6">
      <motion.div
        className="w-full max-w-md rounded-2xl border border-border bg-secondary/40 p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2 className="font-display font-bold text-2xl text-foreground text-center mb-6">
          CPU Match Settings
        </h2>

        <div className="space-y-6">
          <DraftOptions settings={settings} updateSettings={updateSettings} />

          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">CPU Difficulty</p>
            <div className="flex gap-2 justify-center">
              {(['easy', 'hard'] as const).map((difficulty) => (
                <Button
                  key={difficulty}
                  variant={settings.cpuDifficulty === difficulty ? 'default' : 'outline'}
                  onClick={() => updateSettings({ cpuDifficulty: difficulty })}
                  className="capitalize"
                >
                  {difficulty}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">Best Of</p>
            <div className="flex gap-2 justify-center">
              {([3, 5, 10] as const).map((format) => (
                <Button
                  key={format}
                  variant={settings.battleFormat === format ? 'default' : 'outline'}
                  onClick={() => updateSettings({ battleFormat: format })}
                >
                  First to {format}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">Bid Timer</p>
            <div className="flex gap-2 justify-center">
              {[15, 20, 30].map((time) => (
                <Button
                  key={time}
                  variant={settings.bidTimer === time ? 'default' : 'outline'}
                  onClick={() => updateSettings({ bidTimer: time })}
                >
                  {time}s
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Hidden Stat Mode</p>
              <p className="text-xs text-muted-foreground">Stat revealed after you lock pick.</p>
            </div>
            <Switch
              checked={settings.hiddenStat}
              onCheckedChange={(checked) => updateSettings({ hiddenStat: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Rebid Ties</p>
              <p className="text-xs text-muted-foreground">Ties trigger a rebid instead of skip.</p>
            </div>
            <Switch
              checked={settings.rebidMode}
              onCheckedChange={(checked) => updateSettings({ rebidMode: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Coin Rewards</p>
              <p className="text-xs text-muted-foreground">Earn coins for winning battle rounds.</p>
            </div>
            <Switch
              checked={settings.coinRewards}
              onCheckedChange={(checked) => updateSettings({ coinRewards: checked })}
            />
          </div>

          {settings.coinRewards && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 text-center">Reward Amount per Win</p>
              <div className="flex flex-wrap items-center gap-2 justify-center">
                {[300, 500, 750, 1000].map((amount) => (
                  <Button
                    key={amount}
                    variant={settings.coinRewardAmount === amount ? 'default' : 'outline'}
                    onClick={() => updateSettings({ coinRewardAmount: amount })}
                  >
                    {amount}
                  </Button>
                ))}
                <Input
                  type="number"
                  min={0}
                  step={50}
                  value={settings.coinRewardAmount}
                  onChange={(event) => {
                    const parsed = Number(event.target.value);
                    if (!Number.isNaN(parsed)) {
                      updateSettings({ coinRewardAmount: Math.max(0, parsed) });
                    }
                  }}
                  className="w-24 text-center font-mono text-sm"
                />
              </div>
            </div>
          )}

          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">Starting Coins</p>
            <div className="flex flex-wrap items-center gap-2 justify-center">
              {startingCoinOptions.map((amount) => (
                <Button
                  key={amount}
                  variant={settings.startingCoins === amount ? 'default' : 'outline'}
                  onClick={() => updateSettings({ startingCoins: amount })}
                >
                  {amount}
                </Button>
              ))}
              <Input
                type="number"
                min={1000}
                step={500}
                value={settings.startingCoins}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  if (!Number.isNaN(parsed)) {
                    updateSettings({ startingCoins: Math.max(0, parsed) });
                  }
                }}
                className="w-24 text-center font-mono text-sm"
              />
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-2 text-center">Auction Count</p>
            <div className="flex gap-2 justify-center flex-wrap">
              {auctionCountOptions.map((count) => (
                <Button
                  key={count}
                  variant={settings.auctionCount === count ? 'default' : 'outline'}
                  onClick={() => updateSettings({ auctionCount: count })}
                >
                  {count}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-secondary/50 border border-border p-4 text-sm text-muted-foreground">
          <p className="font-display font-semibold text-foreground mb-2 text-center">Settings Summary</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <span>Verse: <span className="text-foreground">{settings.verse}</span></span>
            <span>Bid: <span className="text-foreground">{settings.bidTimer}s</span></span>
            <span>Best of: <span className="text-foreground">{settings.battleFormat}</span></span>
            <span>Coins: <span className="text-foreground">{settings.startingCoins}</span></span>
            <span>Auctions: <span className="text-foreground">{settings.auctionCount}</span></span>
            <span>Hidden Stat: <span className="text-foreground">{settings.hiddenStat ? 'On' : 'Off'}</span></span>
            <span>Rebid: <span className="text-foreground">{settings.rebidMode ? 'On' : 'Off'}</span></span>
          </div>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="outline" onClick={() => setPhase('home')} className="flex-1">
            Back
          </Button>
          <Button
            onClick={handleStart}
            disabled={poolTooSmall}
            className="flex-1 font-display font-bold"
          >
            Start Match
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
